import { useState, useEffect } from 'react'
import { usePermissions } from '../hooks/usePermissions'
import { suscripcionClinicaService } from '../services/suscripcionClinicaService'
import Table, { TableHeader, TableBody, TableRow, TableCell, TableHead } from '../components/ui/Table'
import Button from '../components/ui/Button'
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../components/ui/Modal'
import Badge from '../components/ui/Badge'
import Card from '../components/ui/Card'
import EmptyState from '../components/ui/EmptyState'
import LoadingState from '../components/ui/LoadingState'
import Textarea from '../components/ui/Textarea'
import { 
  CreditCard, RefreshCw, CheckCircle, XCircle, User, Building2, Calendar, AlertCircle, Phone, Mail
} from 'lucide-react'

export default function SuscripcionesPendientes() {
  const { isVeterinarioAdmin } = usePermissions()
  const [suscripciones, setSuscripciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [selectedSuscripcion, setSelectedSuscripcion] = useState(null)
  const [motivoRechazo, setMotivoRechazo] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchSuscripciones()
  }, [])

  const fetchSuscripciones = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await suscripcionClinicaService.getPendientes()
      if (response.success) {
        setSuscripciones(response.data || [])
      } else {
        setError(response.error || 'Error al cargar suscripciones pendientes')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al cargar suscripciones pendientes')
    } finally {
      setLoading(false)
    }
  }

  const handleAprobar = async (id) => {
    if (!confirm('¿Estás seguro de que deseas aprobar esta suscripción?')) {
      return
    }

    try {
      setSubmitting(true)
      setError(null)
      const response = await suscripcionClinicaService.aprobar(id)
      if (response.success) {
        await fetchSuscripciones()
      } else {
        setError(response.error || 'Error al aprobar suscripción')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al aprobar suscripción')
    } finally {
      setSubmitting(false)
    }
  }

  const handleRechazar = async () => {
    if (!selectedSuscripcion) return

    try {
      setSubmitting(true)
      setError(null)
      const response = await suscripcionClinicaService.rechazar(
        selectedSuscripcion.id_suscripcion || selectedSuscripcion.idSuscripcion,
        motivoRechazo || null
      )
      if (response.success) {
        setShowRejectModal(false)
        setSelectedSuscripcion(null)
        setMotivoRechazo('')
        await fetchSuscripciones()
      } else {
        setError(response.error || 'Error al rechazar suscripción')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al rechazar suscripción')
    } finally {
      setSubmitting(false)
    }
  }

  const openRejectModal = (suscripcion) => {
    setSelectedSuscripcion(suscripcion)
    setMotivoRechazo('')
    setShowRejectModal(true)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return <LoadingState />
  }

  if (!isVeterinarioAdmin) {
    return (
      <EmptyState
        icon={CreditCard}
        title="Sin permisos suficientes"
        description="Solo veterinarios administradores de clínica pueden gestionar suscripciones pendientes."
      />
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-pastel-ink dark:text-white">Suscripciones Pendientes</h1>
          <p className="mt-1 text-sm text-pastel-ink/70 dark:text-gray-400">
            Gestiona las solicitudes de suscripción a tu clínica
          </p>
        </div>
        <Button variant="secondary" onClick={fetchSuscripciones} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {suscripciones.length === 0 ? (
        <EmptyState
          icon={CreditCard}
          title="No hay suscripciones pendientes"
          description="No hay solicitudes de suscripción esperando aprobación en este momento."
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuario</TableHead>
                <TableHead className="hidden lg:table-cell">Contacto</TableHead>
                <TableHead className="hidden sm:table-cell">Clínica</TableHead>
                <TableHead className="hidden md:table-cell">Fecha de Solicitud</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead align="right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {suscripciones.map((suscripcion) => {
                const usuario = suscripcion.usuario || {}
                const clinica = suscripcion.clinica || {}
                const idSuscripcion = suscripcion.id_suscripcion || suscripcion.idSuscripcion

                return (
                  <TableRow key={idSuscripcion}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <User className="w-5 h-5 text-pastel-ink/60 dark:text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-pastel-ink dark:text-white">
                            {usuario.nombre} {usuario.apellido || ''}
                          </p>
                          {/* Mostrar email y teléfono en móvil */}
                          <div className="mt-1 space-y-1 lg:hidden">
                            {usuario.email && (
                              <div className="flex items-center space-x-1 text-xs text-pastel-ink/70 dark:text-gray-400">
                                <Mail className="w-3 h-3" />
                                <span>{usuario.email}</span>
                              </div>
                            )}
                            {usuario.telefono && (
                              <div className="flex items-center space-x-1 text-xs text-pastel-ink/70 dark:text-gray-400">
                                <Phone className="w-3 h-3" />
                                <span>{usuario.telefono}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="space-y-2">
                        {usuario.email && (
                          <div className="flex items-center space-x-2">
                            <Mail className="w-4 h-4 text-pastel-ink/60 dark:text-gray-500" />
                            <a 
                              href={`mailto:${usuario.email}`}
                              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              {usuario.email}
                            </a>
                          </div>
                        )}
                        {usuario.telefono && (
                          <div className="flex items-center space-x-2">
                            <Phone className="w-4 h-4 text-pastel-ink/60 dark:text-gray-500" />
                            <a 
                              href={`tel:${usuario.telefono}`}
                              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              {usuario.telefono}
                            </a>
                          </div>
                        )}
                        {!usuario.email && !usuario.telefono && (
                          <span className="text-sm text-pastel-ink/60 dark:text-gray-500">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="flex items-center space-x-2">
                        <Building2 className="w-4 h-4 text-pastel-ink/60 dark:text-gray-500" />
                        <span className="text-sm text-pastel-ink dark:text-white">
                          {clinica.nombre || clinica.name || '-'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-pastel-ink/60 dark:text-gray-500" />
                        <span className="text-sm text-pastel-ink dark:text-white">
                          {formatDate(suscripcion.fecha_suscripcion || suscripcion.fechaSuscripcion)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="warning">Pendiente de Aprobación</Badge>
                    </TableCell>
                    <TableCell align="right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleAprobar(idSuscripcion)}
                          disabled={submitting}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Aprobar
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => openRejectModal(suscripcion)}
                          disabled={submitting}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Rechazar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Modal Rechazar Suscripción */}
      <Modal 
        isOpen={showRejectModal} 
        onClose={() => { 
          setShowRejectModal(false)
          setSelectedSuscripcion(null)
          setMotivoRechazo('')
        }} 
        size="md"
      >
        <ModalHeader onClose={() => { 
          setShowRejectModal(false)
          setSelectedSuscripcion(null)
          setMotivoRechazo('')
        }}>
          Rechazar Suscripción
        </ModalHeader>
        <ModalContent>
          <div className="mb-4">
            <p className="text-sm text-pastel-ink/80 dark:text-gray-400 mb-4">
              ¿Estás seguro de que deseas rechazar esta suscripción? 
              {selectedSuscripcion?.usuario && (
                <span className="font-medium">
                  {' '}Usuario: {selectedSuscripcion.usuario.nombre} {selectedSuscripcion.usuario.apellido || ''}
                </span>
              )}
            </p>
            <Textarea
              label="Motivo del rechazo (opcional)"
              value={motivoRechazo}
              onChange={(e) => setMotivoRechazo(e.target.value)}
              rows={4}
              placeholder="Ingrese el motivo del rechazo..."
            />
          </div>
        </ModalContent>
        <ModalFooter>
          <Button 
            variant="secondary" 
            onClick={() => { 
              setShowRejectModal(false)
              setSelectedSuscripcion(null)
              setMotivoRechazo('')
            }}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={handleRechazar}
            disabled={submitting}
          >
            <XCircle className="w-4 h-4 mr-2" />
            Rechazar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

